import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  ElementRef,
  Injector,
  Optional,
  Self
} from '@angular/core';
import {
  MatFormFieldControl
} from '@angular/material/form-field';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  FocusMonitor
} from '@angular/cdk/a11y';
import {
  NgControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { MaterialModule } from '../../material/material.module';


interface CalendarDay {
  date: Date;
  currentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}
@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, OverlayModule],
  templateUrl: './datetime-picker.html',
  styleUrl: './datetime-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePicker),
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => DatetimePicker),
    }
  ]
})
export class DatetimePicker implements ControlValueAccessor, MatFormFieldControl<Moment | Date | null>, OnDestroy  {
  @Input() placeholder: string = 'Select date and time';
  focused = false;
  stateChanges = new Subject<void>();
  errorState = false;

  controlType = 'app-datetime-picker';

  id = `datetime-picker-${Math.random().toString(36).substring(2)}`;


  required = false;

  disabled = false;


  ngControl: NgControl | null = null;

  // ── Panel state ───────────────────────────────────────────────────────────
  open       = false;
  activeTab: 'date' | 'time' = 'date';
  viewMode:  'day' | 'month' | 'year' = 'day';

  // ── Calendar navigation ───────────────────────────────────────────────────
  viewMonth     = new Date().getMonth();
  viewYear      = new Date().getFullYear();
  yearRangeBase = new Date().getFullYear();


  // ── Internal value (Date, converted to/from Moment on boundary) ───────────
  private _value: Date | null = null;
  value: Moment | Date | null = null;

  // ── Time parts ────────────────────────────────────────────────────────────
  hour24 = 12;
  minute = 0;

  // ── CVA callbacks ─────────────────────────────────────────────────────────
  private onChange: (v: Moment | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ── Static data ───────────────────────────────────────────────────────────
  readonly weekdays = ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'];

  readonly months = [
    'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
    'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef
  ) {}

  get empty(): boolean {
    return this._value == null;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }
  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  setDescribedByIds(ids: string[]): void {}

  onContainerClick(event: MouseEvent): void {
    this.open = true;
  }
    
  ngOnDestroy(): void {
      this.stateChanges.complete();
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  /** Called by Angular when the FormControl value changes externally */
  writeValue2(value: Moment | Date | null): void {
    if (!value) {
      this._value = null;
    } else if (moment.isMoment(value)) {
      this._value = value.toDate();
    } else {
      this._value = new Date(value);
    }

    if (this._value) {
      this.viewMonth     = this._value.getMonth();
      this.viewYear      = this._value.getFullYear();
      this.yearRangeBase = this._value.getFullYear();
      this.hour24        = this._value.getHours();
      this.minute        = this._value.getMinutes();
    }
    this.cdr.markForCheck();
  }

  writeValue(value: Moment | Date | null): void {
    this.value = value;
  
    if (!value) {
      this._value = null;
    } else {
      this._value = moment.isMoment(value)
        ? value.toDate()
        : new Date(value);
    }
  
    this.stateChanges.next(); // IMPORTANT
  }

  registerOnChange(fn: (v: Moment | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(disabled: boolean): void {
    // handled in template via [disabled] if needed
  }

  /** Called by Reactive Forms to run synchronous validation */
  validate(_control: AbstractControl): ValidationErrors | null {
    return null; // cross-field validation stays in the parent FormGroup validator
  }

  // ── Computed helpers ──────────────────────────────────────────────────────

  get selectedDate(): Date | null { return this._value; }



  get displayValue(): string | null {
    if (!this._value) return null;
  
    const d = this._value;
  
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
  
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
  
    return `${day}/${month}/${year}, ${hour}:${minute}`;
  }

  get calendarDays(): CalendarDay[] {
    const year  = this.viewYear;
    const month = this.viewMonth;
    const first = new Date(year, month, 1).getDay();
    const dim   = new Date(year, month + 1, 0).getDate();
    const prev  = new Date(year, month, 0).getDate();
    const today = new Date();
    const sel   = this._value;
    const days: CalendarDay[] = [];

    for (let i = first - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prev - i), currentMonth: false, isToday: false, isSelected: false });
    }
    for (let d = 1; d <= dim; d++) {
      const date = new Date(year, month, d);
      days.push({
        date,
        currentMonth: true,
        isToday:    this.sameDay(date, today),
        isSelected: sel ? this.sameDay(date, sel) : false,
      });
    }
    for (let d = 1; d <= 42 - days.length; d++) {
      days.push({ date: new Date(year, month + 1, d), currentMonth: false, isToday: false, isSelected: false });
    }
    return days;
  }

  get yearRange(): number[] {
    return Array.from({ length: 12 }, (_, i) => this.yearRangeBase - 5 + i);
  }

  // ── Template event handlers ───────────────────────────────────────────────

  toggleOpen(): void {
    this.open = !this.open;
    if (this.open) this.onTouched();
    this.cdr.markForCheck();
  }

  clear(e: Event): void {
    e.stopPropagation();
    this._value = null;
    this.onChange(null);
    this.onTouched();
    this.cdr.markForCheck();
  }

  prevMonth(): void {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
    else { this.viewMonth--; }
    this.cdr.markForCheck();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
    else { this.viewMonth++; }
    this.cdr.markForCheck();
  }

  selectYear(y: number): void {
    this.viewYear = y;
    this.viewMode = 'month';
    this.cdr.markForCheck();
  }

  selectMonth(m: number): void {
    this.viewMonth = m;
    this.viewMode  = 'day';
    this.cdr.markForCheck();
  }

  selectDay(date: Date): void {
    this._value = new Date(date);
    this._value.setHours(this.hour24, this.minute, 0, 0);
    this.activeTab = 'time';
    this.cdr.markForCheck();
  }

  setHour(v: number): void {
    this.hour24 = Math.max(0, Math.min(23, Number(v)));
    this.syncTimeToValue();
  }

  /*setHour12(v: number): void {
    const h = Math.max(1, Math.min(12, Number(v)));
    this.hour24 = this.ampm === 'PM' ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
    this.syncTimeToValue();
  }*/

  setMinute(v: number): void {
    this.minute = Math.max(0, Math.min(59, Number(v)));
    this.syncTimeToValue();
  }



  setNow(): void {
    const now    = new Date();
    this._value  = new Date(now);
    this.viewMonth     = now.getMonth();
    this.viewYear      = now.getFullYear();
    this.yearRangeBase = now.getFullYear();
    this.hour24  = now.getHours();
    this.minute  = now.getMinutes();
    this.cdr.markForCheck();
  }

  apply(): void {
    if (this._value) {
      this._value.setHours(this.hour24, this.minute, 0, 0);
      // Emit as a Moment so the rest of the form (validators, saveClicked) stays unchanged
      this.onChange(moment(this._value));
      this.onTouched();
    }
    this.open = false;
    this.cdr.markForCheck();
  }

  switchView(v: 'day' | 'month' | 'year'): void {
    this.viewMode = v;
    this.cdr.markForCheck();
  }

  switchTab(t: 'date' | 'time'): void {
    this.activeTab = t;
    this.cdr.markForCheck();
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private syncTimeToValue(): void {
    if (this._value) {
      this._value.setHours(this.hour24, this.minute, 0, 0);
    }
    this.cdr.markForCheck();
  }

  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth()    === b.getMonth()
        && a.getDate()     === b.getDate();
  }
}